import * as yup from "yup";

import {
  Button,
  KIND,
  SIZE as BUTTON_SIZE,
  TButtonProps,
} from "kanopi-components/Button";
import React, { FC, useState } from "react";

import { Block } from "kanopi-components/Block";
import { CardBody } from "kanopi-components/Card";
import { Form } from "kanopi-components/Form";
import { FormControl } from "kanopi-components/Input";
import { HeadingSmall, LabelSmall } from "kanopi-components/typography";
import { Select } from "kanopi-components/Select";
import { Modal } from "kanopi-components/Modal";
import { useAPI } from "kw/api/useAPI";
import { useForm } from "react-weaver";
import { useNavigate } from "@reach/router";
import { useGlobal } from "reactn";

type TClaimsPageCreateClaimModal = {
  customers: TCustomer[];
  closeModal: () => void;
};

const ClaimsPageCreateClaimModal: FC<TClaimsPageCreateClaimModal> = ({
  customers,
  closeModal,
}) => {
  const navigate = useNavigate();

  const [, setHijack] = useGlobal("hijack");

  const [policies, setPolicies] = useState([]);

  const { query } = useAPI();

  const getPolicies = (customerId: string) => {
    query(
      {
        admin: {
          customer: [
            {
              id: customerId,
            },
            {
              policies: {
                id: true,
                policyNumber: true,
              },
            },
          ],
        },
      },
      (response: { admin: { customer: { policies: TPolicy[] } } }) => {
        setPolicies(response.admin.customer.policies);
      }
    );
  };

  const { fieldProps, errors, values, notReady } = useForm({
    validator: yup.object().shape({
      customerId: yup.string().required().label("Customer"),
      policyId: yup.string().required().label("Policy"),
    }),
  });

  React.useEffect(() => {
    if (values["customerId"]) {
      getPolicies(values["customerId"]);
    }
  }, [values["customerId"]]);

  const handleSubmit = () => {
    const customerId = values["customerId"];
    const policyId = values["policyId"];
    const nextUrl = `/claims/make-a-claim/${policyId}/intro`;
    setHijack({
      nextUrl,
      releaseToUrl: "/admin/claims",
    });
    navigate(`/user-hijacking/hijack/${customerId}`);
  };

  return (
    <Modal onClose={() => closeModal()} closeable isOpen={true} animate autoFocus>
      <Modal.Body $style={{ marginTop: "64px" }}>
        <Form>
          <CardBody
            display="grid"
            gridGap="scale900"
            gridTemplateColumns={["1fr"]}
            justifyItems="center"
            paddingBottom="scale900"
            paddingTop="scale900"
          >
            <Block>
              <HeadingSmall margin={0}>Create a claim</HeadingSmall>
            </Block>

            <Block width="100%">
              <FormControl label="Customer" error={errors?.policy}>
                <Select
                  id="select-id"
                  options={customers}
                  getOptionLabel={({ option }: { option: TCustomer }) =>
                    `${option.fullName} (${option.email})`
                  }
                  getValueLabel={({ option }: { option: TCustomer }) =>
                    `${option.fullName} (${option.email})`
                  }
                  valueKey="id"
                  placeholder="Choose a customer"
                  {...fieldProps.customerId}
                />
              </FormControl>
              {values["customerId"] ? (
                policies.length > 0 ? (
                  <FormControl label="Policy number" error={errors?.policy}>
                    <Select
                      id="select-id"
                      options={policies}
                      labelKey="policyNumber"
                      valueKey="id"
                      placeholder="Choose a policy"
                      {...fieldProps.policyId}
                    />
                  </FormControl>
                ) : (
                  <Block marginTop={"30px"}>
                    <LabelSmall $style={{ textAlign: "center" }}>
                      This user doesn&apos;t have any policies to claim against.
                    </LabelSmall>
                  </Block>
                )
              ) : null}
            </Block>

            <Button
              type="submit"
              kind={KIND.secondary}
              onClick={handleSubmit}
              disabled={notReady}
            >
              Create a claim
            </Button>
          </CardBody>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

type TClaimsPageCreateClaimButton = {
  customer: TCustomer;
} & TButtonProps;

export const ClaimsPageCreateClaimButton: FC<TClaimsPageCreateClaimButton> = ({
  customer,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const { query } = useAPI();

  const getCustomers = () => {
    query(
      {
        admin: {
          customers: { items: { id: true, fullName: true, email: true } },
        },
      },
      (response: { admin: { customers: { items: TCustomer[] } } }) => {
        setCustomers(response.admin.customers.items);
        setIsOpen(true);
      }
    );
  };

  const handleModalOpen = () => {
    if (customers.length === 0) {
      getCustomers();
    } else {
      setIsOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Button
        size={BUTTON_SIZE.compact}
        outlined={true}
        onClick={handleModalOpen}
        {...props}
      >
        Create claim
      </Button>
      {isOpen && (
        <ClaimsPageCreateClaimModal
          customers={customers}
          closeModal={handleModalClose}
        />
      )}
    </>
  );
};
